import { useState } from 'react'
import { Check, X } from 'lucide-react'
import type { CronMonitorSuccessRateBannerProps } from '../types'
import { buildCronSuccessRateStats } from '../utils/successRate'

export function CronMonitorSuccessRateBanner({
  successful,
  total,
  percent,
  loading = false,
}: CronMonitorSuccessRateBannerProps) {
  const [dismissed, setDismissed] = useState(false)

  if (dismissed) return null

  const stats = buildCronSuccessRateStats(successful, total, percent)
  const percentLabel = loading && successful == null ? '—' : `${stats.percent}%`
  const detailLabel = loading && successful == null
    ? 'Loading run totals…'
    : `${stats.successfulLabel} successful runs out of ${stats.totalLabel} total runs`

  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3"
      role="status"
      aria-live="polite"
      data-testid="cron-monitor-success-rate-banner"
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
        <Check className="h-4 w-4" strokeWidth={3} aria-hidden />
      </div>

      <p className="min-w-0 flex-1 text-sm text-slate-800">
        <span>Success rate (Last 24 hours)</span>
        {' '}
        <span
          className="text-xl font-bold text-emerald-600"
          data-testid="cron-monitor-success-rate-percent"
        >
          {percentLabel}
        </span>
        <span className="text-slate-600">
          {' '}
          •
          {' '}
          <span data-testid="cron-monitor-success-rate-detail">{detailLabel}</span>
        </span>
      </p>

      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="ml-auto inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 transition hover:bg-emerald-100 hover:text-slate-600"
        aria-label="Dismiss success rate"
        data-testid="cron-monitor-success-rate-dismiss"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  )
}
